import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, useMediaQuery, Drawer } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon, Menu as MenuIcon } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import ModalBasicLayout from "./ModalBasicLayout";
import { APP_DROPDOWN_Z_INDEX } from "../../utils/modalLayer";
import ConfigurationPanel from "./ModalCreateWithIA/ConfigurationPanel";
import GeneratedImagesGallery from "./ModalCreateWithIA/GeneratedImagesGallery";
import EmptyState from "./ModalCreateWithIA/EmptyState";
import TrialContentCTAPopup from "../Alerts/TrialContentCTAPopup";
import { useContentModals } from "../../contexts/contentModalsContext";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { useEnsureContentLanguageField } from "../../hooks/useAdvertiserContentLanguage";
import { CONTENT_LANGUAGE_PRESET } from "../../utils/catalogs/contentLanguage";
import { getDefaultAccentForLanguage } from "../../utils/options";
import { DEFAULT_SUBTITLE_OPTIONS } from "./ModalCreateWithIA/subtitlePresets";
import { useMatchCreativeProductOnOpen } from "../../hooks/useMatchCreativeProductOnOpen";
import {
  ALERT_FILL_BRAND_SETUP_DESCRIPTION_FOR_GENERATE_CREATIVES,
  ALERT_FILL_BRAND_SETUP_TITLE,
  BUTTON_COMPLETE_INFORMATION,
  BUTTON_YES_I_SURE,
  BUTTON_GO_BACK,
  ERROR,
  FIELD_REQUIRED,
  CAROUSEL_MIN_IMAGES_WARNING,
  MODAL_CREATE_WITH_IA_ARIA_LABEL,
  MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY,
  MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TITLE,
  MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TEXT,
  MODAL_CREATE_WITH_IA_MAX_CONCURRENT_GENERATIONS,
  MODAL_CREATE_WITH_IA_PHYSICAL_NO_REFERENCE_IMAGES_DESCRIPTION,
  MODAL_CREATE_WITH_IA_YOU_CAN_LEAVE_THE_PAGE,
  WARNING,
  BUTTON_GENERATE_CONTENT,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  startAddContentsToList,
  startGenerateImages,
  startGenerateVideo,
  startGetSubtitlesConfiguration,
} from "../../actions/user";
import { buildAdTemplateGeneratePayload } from "../../utils/adGenerationTemplates";
import {
  checkAndSaveUserOnboardingChecklist,
  checkAndShowNpsForm,
} from "../../actions/adsCampaigns";
import { setModalOpenState } from "../../actions/ui";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import {
  ALERT_ICON_TYPE_ERROR,
  BUSINESS_OFFERING_PHYSICAL_PRODUCT,
  CHECKLIST_CREATE_CONTENT,
  COUNTRIES,
  getReferenceAdStoragePath,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  NPS_RATING_ACTION_GENERATE_9_CONTENTS,
  FILTERS_MEDIA_CONTENT_TYPE,
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE,
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO,
} from "../../utils/constants";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { getIsBlockedActions } from "../../actions/getters";
import { resolveActionCostForPlanType } from "../../utils/creditActionCosts";
import { getUniqueID } from "../../utils/numbers";
import {
  MAX_CONCURRENT_GENERATIONS,
  addPendingReservation,
  applyExternalGenerationSync,
  buildExternalGenerationID,
  buildGenerationPlaceholders,
  buildInitialGeneratedMedia,
  buildRetryParamsFromInitialValues,
  findGeneratedMediaIndex,
  getPendingGenerationIDs,
  getPendingSpendForUsageKey,
  getUnseenMedia,
  isGenerationPlaceholder,
  mapGenerateResponseToMedia,
  releasePendingReservation,
  removeGeneration,
  replaceGenerationPlaceholders,
  stripGenerationPlaceholders,
} from "./ModalCreateWithIA/utils/generationPlaceholders";
import {
  startGetBrandInformation,
  startSaveBrandLookAndFeel,
  startSaveBrandLogo,
} from "../../actions/adsCampaigns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validateModalCreateWithIATextFields } from "../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import { campaignMediaEntriesFromAISaveItems } from "../../services/adsCampaigns";
import { useAdditionalAssetsSelection } from "./ModalCreateWithIA/hooks/useAdditionalAssetsSelection";
import {
  getRemainingReferenceImageSlots,
  mergeReferenceImageUrls,
} from "../../utils/createWithIAReferenceImages";
import { getGalleryIndexedMediaFields } from "../../utils/dimensions";
import {
  scrollToConfigPanelSection,
  REFERENCE_IMAGES_SECTION_ID,
  REFERENCE_AD_SECTION_ID,
} from "./ModalCreateWithIA/utils/configPanelScroll";

import { LaunchError } from "../../utils/errors";

const resolveInitialMediaType = (values = {}) => {
  if (values.mediaType === "video") return "video";
  if (values.isCarousel || values.mediaType === "carousel") return "carousel";
  return values.mediaType || "image";
};

const resolveInitialTemplateID = (values = {}) => {
  const templateID =
    typeof values.templateID === "string" ? values.templateID.trim() : "";
  if (templateID) return templateID;
  const nestedTemplateID = values.adTemplate?.templateID;
  return typeof nestedTemplateID === "string" ? nestedTemplateID.trim() : "";
};

const getAspectRatioOptionsForMediaType = (mediaType) =>
  mediaType === "video"
    ? MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO
    : MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE;

const isValidAspectRatioForMediaType = (mediaType, aspectRatio) =>
  getAspectRatioOptionsForMediaType(mediaType).some(
    (option) => option.value === aspectRatio
  );

const getFallbackAspectRatioForMediaType = (mediaType) =>
  mediaType === "image" || mediaType === "carousel" ? "1:1" : "9:16";

const getGalleryContentTypeFromMediaType = (mediaType) => {
  switch (mediaType) {
    case "video":
      return FILTERS_MEDIA_CONTENT_TYPE.video;
    case "carousel":
      return FILTERS_MEDIA_CONTENT_TYPE.carousel;
    case "image":
      return FILTERS_MEDIA_CONTENT_TYPE.image;
    default: {
      const exhaustiveCheck = mediaType;
      void exhaustiveCheck;
      return FILTERS_MEDIA_CONTENT_TYPE.image;
    }
  }
};

const resolveAspectRatioForMediaType = (mediaType, aspectRatio) =>
  isValidAspectRatioForMediaType(mediaType, aspectRatio)
    ? aspectRatio
    : getFallbackAspectRatioForMediaType(mediaType);

const ModalCreateWithIA = ({
  modalOpen,
  onCloseModal,
  initialValues = {},
  onClosePreviousComponents = () => {},
  doReUpdateInitialValues = false,
  onCallbackCampaignMediaImport = undefined,
  onCallbackAfterSaveToGallery = undefined,
  contentFolderID = null,
}) => {
  const { openGallery, openShare, close } = useContentModals();
  const nestedModalIdsRef = useRef([]);
  const isMountedRef = useRef(true);
  const { t, i18n } = useTranslationApp();
  const {
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
    ADS_CAMPAIGN_GENERATE_VIDEO_ACTION,
  } = useCreditActionCosts();
  const initialMediaType = resolveInitialMediaType(initialValues);
  const defaultAspectRatio = resolveAspectRatioForMediaType(
    initialMediaType,
    initialValues.aspectRatio
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState,
  } = useForm({
    defaultValues: {
      mediaType: initialMediaType,
      videoType: initialValues.videoType || "ugc",
      duration: initialValues.duration || 30,
      hasText: initialValues.hasText ?? true,
      hasSubtitles: initialValues.hasSubtitles ?? false,
      hasWatermark: initialValues.hasWatermark ?? false,
      hasLogo: initialValues.hasLogo ?? false,
      aspectRatio: defaultAspectRatio,
      language: initialValues.language || "",
      accent:
        initialValues.accent ||
        getDefaultAccentForLanguage(initialValues.language || "en"),
      prompt: initialValues.prompt || "",
      imagesUrl: initialValues.imagesUrl || [],
      websiteUrl: initialValues.websiteUrl || "",
      businessLookAndFeel: initialValues.businessLookAndFeel || "",
      platform: initialValues.platform || "default",
      isCarousel: initialValues.isCarousel ?? false,
      numberOfImages: initialValues.numberOfImages ?? 3,
      subtitleOptions:
        initialValues.subtitleOptions || DEFAULT_SUBTITLE_OPTIONS,
      imageLocation: initialValues.imageLocation || "",
      referenceAdUrl: initialValues.referenceAdUrl || "",
      referenceAdMediaType: initialValues.referenceAdMediaType || "",
      templateID: resolveInitialTemplateID(initialValues),
    },
    mode: "onChange",
  });

  useFocusErrorForm(formState);
  const { errors } = formState;

  const [brandInformation, setBrandInformation] = useState({});
  const {
    assets: additionalAssets,
    enabledMap: additionalAssetsEnabledMap,
    activeAssets: activeAdditionalAssets,
    onLoadAssets: onLoadAdditionalAssets,
    onToggleAsset: onToggleAdditionalAsset,
    onRestoreEnabledMapFromSentAssets: onRestoreAdditionalAssetsFromSent,
  } = useAdditionalAssetsSelection();
  const externalGenerationIDRef = useRef(
    initialValues?.isGenerating === true
      ? buildExternalGenerationID(initialValues)
      : null
  );
  const [generatedMedia, setGeneratedMedia] = useState(() =>
    buildInitialGeneratedMedia({
      generatedMedia: initialValues.generatedMedia,
      isGenerating: initialValues.isGenerating,
      generationID: externalGenerationIDRef.current,
      mediaType: initialMediaType,
      aspectRatio: defaultAspectRatio,
      numberOfImages: initialValues.numberOfImages,
    })
  );
  const [ratings, setRatings] = useState(initialValues.ratings || {});
  const [isPreparingSubmit, setIsPreparingSubmit] = useState(false);
  const generatedMediaRef = useRef(generatedMedia);
  const ratingsRef = useRef(ratings);
  const isPreparingSubmitRef = useRef(false);
  const generationParamsRef = useRef(new Map());
  const pendingReservationsRef = useRef([]);
  const beginTrackedGenerationRef = useRef(null);
  const dismissedExternalIDsRef = useRef(new Set());
  const hydratedIncomingKeyRef = useRef(null);
  generatedMediaRef.current = generatedMedia;
  ratingsRef.current = ratings;
  const [selectedImages, setSelectedImages] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [trialCTAPopupOpen, setTrialCTAPopupOpen] = useState(false);
  const [trialCTACreatives, setTrialCTACreatives] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isConfigOpen, setIsConfigOpen] = useState(true);
  const [shouldExpandAdvancedAccordion, setShouldExpandAdvancedAccordion] =
    useState(false);
  const toggleConfig = () => setIsConfigOpen((prev) => !prev);
  const galleryPanelRef = useRef(null);
  const generatedHighlightTimeoutRef = useRef(null);
  const detachFromIncomingInitialValuesRef = useRef(false);
  const [highlightedGalleryKeys, setHighlightedGalleryKeys] = useState([]);

  const notifyGeneratedGallerySaved = useCallback((keys = []) => {
    if (!Array.isArray(keys) || keys.length === 0) {
      return;
    }

    setHighlightedGalleryKeys(keys.map(String));
    if (generatedHighlightTimeoutRef.current) {
      clearTimeout(generatedHighlightTimeoutRef.current);
    }
    generatedHighlightTimeoutRef.current = setTimeout(() => {
      setHighlightedGalleryKeys([]);
    }, 5000);

    setTimeout(() => {
      galleryPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (generatedHighlightTimeoutRef.current) {
        clearTimeout(generatedHighlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const nestedModalIds = nestedModalIdsRef;
    return () => {
      nestedModalIds.current.forEach((id) => close(id));
      nestedModalIds.current = [];
    };
  }, [close]);

  const focusReferenceImagesSection = useCallback(() => {
    setIsConfigOpen(true);
    scrollToConfigPanelSection({ sectionId: REFERENCE_IMAGES_SECTION_ID });
  }, []);

  const dispatch = useDispatchApp();

  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );
  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const typeOfPlans = useSelectorApp((state) => state?.payments?.typeOfPlans);

  const updateGeneratedMedia = useCallback((updater) => {
    setGeneratedMedia((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      generatedMediaRef.current = next;
      return next;
    });
  }, []);

  const [params] = useSearchParams();
  const campaignID = params.get("campaignID");

  useEffect(() => {
    dispatch(setModalOpenState("isModalCreateWithIA", modalOpen));
    return () => {
      dispatch(setModalOpenState("isModalCreateWithIA", false));
    };
  }, [modalOpen, dispatch]);

  useEffect(() => {
    if (modalOpen) {
      setIsConfigOpen(true);
    }
    // eslint-disable-next-line
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    if (!initialValues?.referenceAdUrl) return;

    setIsConfigOpen(true);
    setShouldExpandAdvancedAccordion(true);
    scrollToConfigPanelSection({ sectionId: REFERENCE_AD_SECTION_ID });
  }, [modalOpen, initialValues?.referenceAdUrl]);

  // Mirrors the notification's job into the local gallery. Every step is
  // idempotent (placeholders, unseen-media merge, failed-card seed), so a
  // rebuilt `initialValues` object with the same content is a no-op.
  useEffect(() => {
    if (!doReUpdateInitialValues) return;
    if (!initialValues || Object.keys(initialValues).length === 0) return;

    const incomingIsGenerating = initialValues.isGenerating === true;
    const incomingGenerationID = buildExternalGenerationID(initialValues);

    if (dismissedExternalIDsRef.current.has(incomingGenerationID)) {
      externalGenerationIDRef.current = null;
      generationParamsRef.current.delete(incomingGenerationID);
      return;
    }

    if (incomingIsGenerating && !externalGenerationIDRef.current) {
      externalGenerationIDRef.current = incomingGenerationID;
    }

    const wasTracked = Boolean(externalGenerationIDRef.current);
    const hasFailureToShow =
      !incomingIsGenerating &&
      Boolean(initialValues.errorMessage || initialValues.alertClosed === true);
    if (!incomingIsGenerating && !wasTracked && !hasFailureToShow) return;

    const generationID =
      externalGenerationIDRef.current || incomingGenerationID;

    if (!generationParamsRef.current.has(generationID)) {
      const stored = buildRetryParamsFromInitialValues(initialValues);
      if (stored) {
        generationParamsRef.current.set(generationID, stored);
      }
    }

    const nextMediaType = resolveInitialMediaType(initialValues);
    const canRetry = generationParamsRef.current.has(generationID);
    const placeholders = buildGenerationPlaceholders({
      generationID,
      mediaType: nextMediaType,
      aspectRatio: resolveAspectRatioForMediaType(
        nextMediaType,
        initialValues.aspectRatio
      ),
      numberOfImages: initialValues.numberOfImages,
      canDismiss: true,
      canRetry,
    });

    updateGeneratedMedia((prev) => {
      const synced = applyExternalGenerationSync({
        items: prev,
        generationID,
        isGenerating: incomingIsGenerating,
        incomingMedia: initialValues.generatedMedia || [],
        placeholders,
        errorMessage:
          initialValues.jobResponse?.message ||
          initialValues.errorMessage ||
          "",
        canRetry,
        seedFailure: hasFailureToShow,
      });
      if (synced.didInsertResults) {
        generationParamsRef.current.delete(generationID);
      }
      return synced.items;
    });

    if (!incomingIsGenerating) {
      if (wasTracked && initialValues.ratings) {
        setRatings((prev) => ({ ...prev, ...initialValues.ratings }));
      }
      externalGenerationIDRef.current = null;
    }
  }, [initialValues, doReUpdateInitialValues, updateGeneratedMedia]);

  // Seeds the form once per notification job. Skipped after the user edits the
  // form or submits, so later job syncs never overwrite their work.
  useEffect(() => {
    if (!doReUpdateInitialValues) return;
    if (!initialValues || Object.keys(initialValues).length === 0) return;
    if (detachFromIncomingInitialValuesRef.current) return;
    if (formState.isDirty) return;

    const incomingIsGenerating = initialValues.isGenerating === true;
    const incomingDismissed = dismissedExternalIDsRef.current.has(
      buildExternalGenerationID(initialValues)
    );
    const incomingJobKey = `${initialValues.jobID || ""}:${initialValues.alertId || ""}`;
    const shouldHydrateForm =
      hydratedIncomingKeyRef.current !== incomingJobKey &&
      initialValues.alertClosed !== true;

    if (shouldHydrateForm) {
      hydratedIncomingKeyRef.current = incomingJobKey;
      const currentValues = getValues();
      const nextMediaType = resolveInitialMediaType({
        mediaType: initialValues.mediaType || currentValues.mediaType,
        isCarousel: initialValues.isCarousel ?? currentValues.isCarousel,
      });
      setValue("mediaType", nextMediaType);
      setValue("videoType", initialValues.videoType || currentValues.videoType);
      setValue("duration", initialValues.duration || currentValues.duration);
      setValue("hasText", initialValues.hasText ?? currentValues.hasText);
      setValue(
        "hasSubtitles",
        initialValues.hasSubtitles ?? currentValues.hasSubtitles
      );
      setValue(
        "hasWatermark",
        initialValues.hasWatermark ?? currentValues.hasWatermark
      );
      setValue("hasLogo", initialValues.hasLogo ?? currentValues.hasLogo);
      setValue(
        "aspectRatio",
        resolveAspectRatioForMediaType(
          nextMediaType,
          initialValues.aspectRatio || currentValues.aspectRatio
        )
      );
      setValue("language", initialValues.language || currentValues.language);
      setValue(
        "accent",
        initialValues.accent ||
          currentValues.accent ||
          getDefaultAccentForLanguage(
            initialValues.language || currentValues.language || "en"
          )
      );
      setValue("prompt", initialValues.prompt || currentValues.prompt);
      setValue("imagesUrl", initialValues.imagesUrl || currentValues.imagesUrl);
      setValue(
        "websiteUrl",
        initialValues.websiteUrl || currentValues.websiteUrl
      );
      setValue(
        "businessLookAndFeel",
        initialValues.businessLookAndFeel || currentValues.businessLookAndFeel
      );
      setValue(
        "platform",
        initialValues.platform || currentValues.platform || "default"
      );
      setValue(
        "isCarousel",
        initialValues.isCarousel ?? currentValues.isCarousel ?? false
      );
      setValue(
        "numberOfImages",
        initialValues.numberOfImages ?? currentValues.numberOfImages ?? 3
      );
      setValue(
        "subtitleOptions",
        initialValues.subtitleOptions ||
          currentValues.subtitleOptions ||
          DEFAULT_SUBTITLE_OPTIONS
      );
      setValue(
        "imageLocation",
        initialValues.imageLocation ?? currentValues.imageLocation ?? ""
      );
      setValue(
        "referenceAdUrl",
        initialValues.referenceAdUrl ?? currentValues.referenceAdUrl ?? ""
      );
      setValue(
        "referenceAdMediaType",
        initialValues.referenceAdMediaType ??
          currentValues.referenceAdMediaType ??
          ""
      );
      setValue(
        "templateID",
        resolveInitialTemplateID(initialValues) ||
          currentValues.templateID ||
          ""
      );

      if (
        !incomingIsGenerating &&
        !incomingDismissed &&
        initialValues.generatedMedia
      ) {
        updateGeneratedMedia((prev) => {
          const incomingReal = stripGenerationPlaceholders(
            initialValues.generatedMedia
          );
          const fresh = getUnseenMedia({
            candidates: incomingReal,
            existing: stripGenerationPlaceholders(prev),
          });
          if (fresh.length === 0) return prev;
          const localPending = prev.filter((item) =>
            isGenerationPlaceholder(item)
          );
          const localReal = prev.filter(
            (item) => !isGenerationPlaceholder(item)
          );
          return [...localPending, ...fresh, ...localReal];
        });
      }

      if (Array.isArray(initialValues.additionalAssets)) {
        onRestoreAdditionalAssetsFromSent({
          sentAssets: initialValues.additionalAssets,
        });
      }
    }
    //eslint-disable-next-line
  }, [initialValues]);

  useEffect(() => {
    if (!modalOpen) {
      setShouldExpandAdvancedAccordion(false);
    }
  }, [modalOpen]);

  useMatchCreativeProductOnOpen({
    modalOpen,
    getValues,
    setValue,
    campaignID,
    initialValues,
    enabled: !doReUpdateInitialValues,
    onProductMatched: () => setShouldExpandAdvancedAccordion(true),
  });

  const imagesUrl = watch("imagesUrl") || [];
  const mediaType = watch("mediaType");
  const aspectRatio = watch("aspectRatio");
  const numberOfImages = watch("numberOfImages");
  const hasText = watch("hasText");

  useEnsureContentLanguageField({
    preset:
      mediaType === "video"
        ? CONTENT_LANGUAGE_PRESET.VIDEO
        : CONTENT_LANGUAGE_PRESET.IMAGE,
    setValue,
    getValues,
    contextLanguage: initialValues.language || null,
    enabled: modalOpen && (mediaType === "video" || !!hasText),
  });

  useEffect(() => {
    const currentAspectRatio = getValues("aspectRatio");
    if (isValidAspectRatioForMediaType(mediaType, currentAspectRatio)) {
      return;
    }
    setValue("aspectRatio", getFallbackAspectRatioForMediaType(mediaType));
    // eslint-disable-next-line
  }, [mediaType]);

  const navigate = useNavigate();
  const { userID } = dispatch(getPathDatabaseByRole());
  const hasReferenceImage = imagesUrl.length > 0;

  const hasBrandContext = Boolean(
    brandInformation.businessName && brandInformation.businessDescription
  );
  const getBrandInformation = async () => {
    const response = await dispatch(startGetBrandInformation());
    if (response.ok) {
      const { data } = response;
      setBrandInformation(data);
      setValue("businessLookAndFeel", data?.businessLookAndFeel || "");
      setValue("imageLocation", data?.businessCountry || "");
      onLoadAdditionalAssets({
        assets:
          data?.hasAdditionalAssets === true ? data?.additionalAssets : [],
        ...(data?.hasAdditionalAssets === true &&
          Array.isArray(initialValues?.additionalAssets) && {
            sentAssets: initialValues.additionalAssets,
          }),
      });
    }
  };
  const getSubtitlesConfiguration = async () => {
    const data = await dispatch(startGetSubtitlesConfiguration());
    if (data) {
      setValue("subtitleOptions", data);
    }
  };

  const onSaveLogo = async (logoUrl) => {
    try {
      await dispatch(startSaveBrandLogo(logoUrl));
      setBrandInformation((prev) => ({ ...prev, logoUrl }));
    } catch (e) {
      dispatch(
        LaunchError(e, false, [], {
          functionName: "onSaveLogo",
          capture: "exception",
        })
      );
      console.log(e);
    }
  };

  const releasePrepareLock = () => {
    isPreparingSubmitRef.current = false;
    if (isMountedRef.current) {
      setIsPreparingSubmit(false);
    }
  };

  const alertIfAtConcurrencyLimit = async () => {
    const pendingCount = getPendingGenerationIDs(
      generatedMediaRef.current
    ).size;
    if (pendingCount < MAX_CONCURRENT_GENERATIONS) return false;

    await SimpleAlert({
      text: t(MODAL_CREATE_WITH_IA_MAX_CONCURRENT_GENERATIONS, {
        count: MAX_CONCURRENT_GENERATIONS,
      }),
      icon: "info",
    });
    return true;
  };

  // Single ledger for every operation that spends credits (generate, resize,
  // animate). A balance drop is not attributed across jobs: each operation
  // releases only its own reservation.
  const getPendingSpendForAction = useCallback(
    (action) => {
      const resolved = resolveActionCostForPlanType(action, typeOfPlans);
      return getPendingSpendForUsageKey({
        reservations: pendingReservationsRef.current,
        usageKey: resolved?.usageKey,
      });
    },
    [typeOfPlans]
  );

  const onReserveActionSpend = useCallback(
    ({ reservationID, action, multiplier = 1 }) => {
      const resolved = resolveActionCostForPlanType(action, typeOfPlans);
      pendingReservationsRef.current = addPendingReservation({
        reservations: pendingReservationsRef.current,
        reservationID,
        usageKey: resolved?.usageKey,
        amount: (Number(resolved?.amount) || 0) * (Number(multiplier) || 0),
      });
    },
    [typeOfPlans]
  );

  const onReleaseActionSpend = useCallback(({ reservationID }) => {
    pendingReservationsRef.current = releasePendingReservation({
      reservations: pendingReservationsRef.current,
      reservationID,
    });
  }, []);

  const scrollGalleryToTop = () => {
    setTimeout(() => {
      galleryPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  const finishTrackedGeneration = async ({
    generationID,
    generateParams,
    isVideo,
  }) => {
    try {
      const response = await dispatch(
        isVideo
          ? startGenerateVideo(generateParams)
          : startGenerateImages(generateParams)
      );
      if (!isMountedRef.current) return;

      if (response?.ok) {
        const { media, newRatings } = mapGenerateResponseToMedia({
          response,
          mediaType: isVideo ? "video" : "image",
        });
        if (newRatings && Object.keys(newRatings).length > 0) {
          setRatings((prev) => ({ ...prev, ...newRatings }));
        }

        if (!media.length) {
          updateGeneratedMedia((prev) =>
            removeGeneration({ items: prev, generationID })
          );
          return;
        }

        updateGeneratedMedia((prev) =>
          replaceGenerationPlaceholders({
            items: prev,
            generationID,
            media,
          })
        );

        const usedCreatives =
          (subscriptionPlanInfo?.benefits?.creatives || 0) -
          (subscriptionPlanInfo?.usage?.creatives || 0);
        const usedVideos =
          (subscriptionPlanInfo?.benefits?.videos || 0) -
          (subscriptionPlanInfo?.usage?.videos || 0);
        if (usedCreatives + usedVideos >= 9) {
          dispatch(checkAndShowNpsForm(NPS_RATING_ACTION_GENERATE_9_CONTENTS));
        }
        dispatch(checkAndSaveUserOnboardingChecklist(CHECKLIST_CREATE_CONTENT));
        return;
      }

      updateGeneratedMedia((prev) =>
        removeGeneration({ items: prev, generationID })
      );
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "finishTrackedGeneration",
          capture: "exception",
        })
      );
      console.log(error);
      if (!isMountedRef.current) return;
      updateGeneratedMedia((prev) =>
        removeGeneration({ items: prev, generationID })
      );
    } finally {
      onReleaseActionSpend({ reservationID: generationID });
    }
  };

  const beginTrackedGeneration = async ({
    generateParams,
    mediaType,
    aspectRatio,
    numberOfImages,
    multiplier,
    isVideo,
    replaceGenerationID = null,
    onQueued = () => {},
  }) => {
    const action = isVideo
      ? ADS_CAMPAIGN_GENERATE_VIDEO_ACTION
      : ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION;

    if (await alertIfAtConcurrencyLimit()) return;
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action,
        showUsageConfirmationAlert: true,
        multiplier,
        pendingSpend: getPendingSpendForAction(action),
      })
    );
    if (isBlocked || !isMountedRef.current) return;

    const generationID = getUniqueID();
    const placeholders = buildGenerationPlaceholders({
      generationID,
      mediaType,
      aspectRatio,
      numberOfImages,
    });

    generationParamsRef.current.set(generationID, {
      generateParams,
      mediaType,
      aspectRatio,
      numberOfImages,
      multiplier,
      isVideo,
    });
    if (replaceGenerationID) {
      generationParamsRef.current.delete(replaceGenerationID);
      // A retried notification job must not be re-seeded by the next sync.
      dismissedExternalIDsRef.current.add(replaceGenerationID);
    }

    onReserveActionSpend({ reservationID: generationID, action, multiplier });

    detachFromIncomingInitialValuesRef.current = true;
    updateGeneratedMedia((prev) => {
      if (replaceGenerationID) {
        return replaceGenerationPlaceholders({
          items: prev,
          generationID: replaceGenerationID,
          media: placeholders,
        });
      }
      return [...placeholders, ...prev];
    });
    scrollGalleryToTop();

    // The submit lock only covers validation and queueing; the request itself
    // runs unlocked so the next generation can start right away.
    onQueued();
    await finishTrackedGeneration({ generationID, generateParams, isVideo });
  };

  beginTrackedGenerationRef.current = beginTrackedGeneration;

  const runWithSubmitLock = async (start) => {
    if (isPreparingSubmitRef.current) return;
    isPreparingSubmitRef.current = true;
    if (isMountedRef.current) setIsPreparingSubmit(true);

    try {
      await start({ onQueued: releasePrepareLock });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "runWithSubmitLock",
          capture: "exception",
        })
      );
      console.log(error);
    } finally {
      releasePrepareLock();
    }
  };

  const onRetryGeneration = useCallback(async ({ generationID }) => {
    const stored = generationParamsRef.current.get(generationID);
    if (!stored) return;

    await runWithSubmitLock(({ onQueued }) =>
      beginTrackedGenerationRef.current({
        ...stored,
        replaceGenerationID: generationID,
        onQueued,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDetachIncomingInitialValues = useCallback(() => {
    detachFromIncomingInitialValuesRef.current = true;
  }, []);

  const onDismissGeneration = useCallback(
    ({ generationID }) => {
      dismissedExternalIDsRef.current.add(generationID);
      if (externalGenerationIDRef.current === generationID) {
        externalGenerationIDRef.current = null;
      }
      generationParamsRef.current.delete(generationID);
      onReleaseActionSpend({ reservationID: generationID });
      updateGeneratedMedia((prev) =>
        removeGeneration({ items: prev, generationID })
      );
    },
    [updateGeneratedMedia, onReleaseActionSpend]
  );

  const onSubmit = () => runWithSubmitLock(prepareGenerationSubmit);

  const prepareGenerationSubmit = async ({ onQueued }) => {
    const data = getValues();
    const imagesUrl = data.imagesUrl || [];
    if (!isValidAspectRatioForMediaType(data.mediaType, data.aspectRatio)) {
      setError("aspectRatio", {
        type: "validate",
        message: t(FIELD_REQUIRED),
      });
      return;
    }

    if (data.mediaType === "carousel") {
      const carouselImageCount = parseInt(data.numberOfImages, 10);
      if (Number.isNaN(carouselImageCount) || carouselImageCount < 2) {
        setError("numberOfImages", {
          type: "min",
          message: t(CAROUSEL_MIN_IMAGES_WARNING),
        });
        return;
      }
    }

    try {
      validateModalCreateWithIATextFields({
        formData: {
          prompt: data.prompt,
          businessLookAndFeel: data.businessLookAndFeel,
          websiteUrl: data.websiteUrl,
          referenceAdUrl: data.referenceAdUrl,
        },
        isPlanActive,
        isTrial,
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "prepareGenerationSubmit",
          capture: "exception",
        })
      );
      SimpleAlert({
        title: t(ERROR),
        text: error?.message || "The request could not be processed.",
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return;
    }

    const isPhysicalBrand =
      brandInformation?.businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT;

    if (isPhysicalBrand && imagesUrl.length === 0) {
      const physicalReferenceAlert = await ConfirmAlert({
        title: t(WARNING),
        text: t(MODAL_CREATE_WITH_IA_PHYSICAL_NO_REFERENCE_IMAGES_DESCRIPTION),
        confirmButtonText: t(BUTTON_GO_BACK),
        cancelButtonText: t(BUTTON_GENERATE_CONTENT),
        showCancelButton: true,
        icon: "warning",
      });

      if (physicalReferenceAlert.isConfirmed) {
        focusReferenceImagesSection();
        return;
      }

      const didChooseGenerate =
        physicalReferenceAlert.isDismissed &&
        physicalReferenceAlert.dismiss === "cancel";

      if (!didChooseGenerate) {
        return;
      }
    }

    try {
      const currentBLF = getValues("businessLookAndFeel") ?? "";
      dispatch(startSaveBrandLookAndFeel(currentBLF));
    } catch (e) {
      dispatch(
        LaunchError(e, false, [], {
          functionName: "prepareGenerationSubmit",
          capture: "exception",
        })
      );
      console.log(e);
    }

    const actualMediaType =
      data.mediaType === "carousel" ? "image" : data.mediaType;
    const isCarouselMode = data.mediaType === "carousel";
    const isVideo = actualMediaType === "video";

    let parsedNumberOfImages = parseInt(data.numberOfImages, 10);
    if (isNaN(parsedNumberOfImages)) {
      parsedNumberOfImages = 3;
    }
    if (parsedNumberOfImages < 1) {
      parsedNumberOfImages = 1;
    }
    if (parsedNumberOfImages > 10) {
      parsedNumberOfImages = 10;
    }

    let multiplier;
    if (isVideo) {
      multiplier = 1;
    } else {
      multiplier = parsedNumberOfImages || 1;
    }

    if (!hasBrandContext) {
      const response = await ConfirmAlert({
        title: t(ALERT_FILL_BRAND_SETUP_TITLE),
        text: t(ALERT_FILL_BRAND_SETUP_DESCRIPTION_FOR_GENERATE_CREATIVES),
        showCancelButton: false,
        confirmButtonText: t(BUTTON_COMPLETE_INFORMATION),
      });
      if (response.isConfirmed) {
        onCloseModal();
        onClosePreviousComponents();
        navigate("/settings");
        return;
      }

      return;
    }

    try {
      const countryOption = data.imageLocation
        ? COUNTRIES.find((c) => c.value === data.imageLocation)
        : null;
      const locationName = countryOption ? t(countryOption.label) : undefined;
      const adTemplate =
        actualMediaType === "video"
          ? null
          : buildAdTemplateGeneratePayload({
              templateID: data.templateID,
            });

      const generateParams = {
        prompt: data.prompt,
        language: data.hasText ? data.language : undefined,
        uiLanguage: i18n.language,
        ...(actualMediaType === "video" && {
          accent: data.accent,
          hasWatermark: data.hasWatermark,
        }),
        ...(actualMediaType === "image" &&
          locationName && { location: locationName }),
        size: data.dimensions,
        websiteUrl: data.websiteUrl,
        imagesUrl: mergeReferenceImageUrls({
          currentUrls: [],
          newUrls: data.imagesUrl || [],
          mediaType: data.mediaType,
        }),
        mediaType: actualMediaType,
        hasText: data.hasText,
        hasSubtitles: data.hasSubtitles,
        ...(data.hasSubtitles &&
          data.subtitleOptions && {
            subtitleOptions: data.subtitleOptions,
          }),
        logoUrl:
          !isVideo && data.hasLogo && brandInformation?.logoUrl
            ? brandInformation.logoUrl
            : undefined,
        hasLogo: !isVideo && Boolean(data.hasLogo),
        campaignID,
        campaignContext: initialValues.campaignContext,
        aspectRatio: data.aspectRatio,
        videoType: data.videoType,
        duration: data.duration,
        generatedMedia: stripGenerationPlaceholders(generatedMediaRef.current),
        ratings: ratingsRef.current,
        platform: data.platform,
        ...(adTemplate && { adTemplate }),
        ...(actualMediaType === "image" && {
          isCarousel: isCarouselMode,
          numberOfImages: parsedNumberOfImages,
          ...(data.referenceAdUrl && {
            referenceAdUrl: data.referenceAdUrl,
          }),
        }),
        ...(actualMediaType === "video" &&
          data.referenceAdUrl && {
            referenceAdUrl: data.referenceAdUrl,
            referenceAdMediaType: data.referenceAdMediaType || "video",
          }),
        ...(actualMediaType !== "video" &&
          brandInformation?.hasAdditionalAssets === true &&
          activeAdditionalAssets.length > 0 && {
            additionalAssets: activeAdditionalAssets,
          }),
      };

      return beginTrackedGeneration({
        generateParams,
        mediaType: data.mediaType,
        aspectRatio: data.aspectRatio,
        numberOfImages: parsedNumberOfImages,
        multiplier,
        isVideo,
        onQueued,
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "prepareGenerationSubmit",
          capture: "exception",
        })
      );
      console.log(error);
    }
  };

  const onGallerySelection = (selectedItems, target) => {
    if (selectedItems && selectedItems.length > 0) {
      const imageUrls = selectedItems.map((item) => item.url || item.src);

      if (target === "referenceAd") {
        const firstUrl = imageUrls[0];
        if (firstUrl) {
          setValue("referenceAdUrl", firstUrl);
          setValue(
            "referenceAdMediaType",
            getValues("mediaType") === "video" ? "video" : "image"
          );
        }
        setIsGalleryOpen(false);
        return;
      }

      const currentUrls = getValues("imagesUrl") || [];
      setValue(
        "imagesUrl",
        mergeReferenceImageUrls({
          currentUrls,
          newUrls: imageUrls,
          mediaType: getValues("mediaType"),
        })
      );
    }

    setIsGalleryOpen(false);
  };

  const openReferenceGallery = (target) => {
    if (isGalleryOpen) {
      return;
    }

    const isReferenceAd = target === "referenceAd";
    setIsGalleryOpen(true);
    const galleryId = openGallery({
      skipAutoSelectOnUpload: true,
      onCallbackSelectedItems: (selectedItems) =>
        onGallerySelection(selectedItems, target),
      multiple: !isReferenceAd,
      showAdAccountOption: true,
      showSocialPostsOption: true,
      showUploadDeviceOption: true,
      pathStorage: isReferenceAd
        ? getReferenceAdStoragePath(userID)
        : `socialMedia/${userID}/`,
      acceptedFormats:
        isReferenceAd && mediaType === "video" ? VIDEO_FORMATS : IMAGE_FORMATS,
      maxSelections: isReferenceAd
        ? 1
        : getRemainingReferenceImageSlots({
            currentCount: imagesUrl.length,
            mediaType,
          }),
      onCloseModal: () => {
        setIsGalleryOpen(false);
        nestedModalIdsRef.current = nestedModalIdsRef.current.filter(
          (id) => id !== galleryId
        );
      },
    });
    nestedModalIdsRef.current.push(galleryId);
  };

  const onOpenGallery = () => {
    openReferenceGallery("referenceImages");
  };

  const onOpenGalleryForReferenceAd = () => {
    openReferenceGallery("referenceAd");
  };

  const onSelectMediaToggle = (index) => {
    const mediaItem = generatedMedia[index];
    if (!mediaItem || isGenerationPlaceholder(mediaItem)) return;

    // Caso: Objeto agrupado (type: carousel)
    if (mediaItem.type === "carousel" && Array.isArray(mediaItem.urls)) {
      const carouselUrls = mediaItem.urls;
      // Verificar si todas las imágenes del carousel están seleccionadas
      const allSelected = carouselUrls.every((u) => selectedImages.includes(u));

      setSelectedImages((prev) => {
        if (allSelected) {
          // Deseleccionar todas
          return prev.filter((u) => !carouselUrls.includes(u));
        } else {
          // Seleccionar todas (agregando las que falten)
          const newUrls = carouselUrls.filter((u) => !prev.includes(u));
          return [...prev, ...newUrls];
        }
      });
      return;
    }

    const url = typeof mediaItem === "string" ? mediaItem : mediaItem.url;
    if (!url) return;

    // Verificar si esta imagen pertenece a un carousel (Lógica Legacy)
    const carouselID =
      typeof mediaItem === "object" && mediaItem.carouselID
        ? mediaItem.carouselID
        : null;

    if (carouselID) {
      // Si pertenece a un carousel, seleccionar/deseleccionar todas las imágenes del carousel
      const carouselImages = generatedMedia.filter((media) => {
        const mediaCarouselID =
          typeof media === "object" && media.carouselID
            ? media.carouselID
            : null;
        return mediaCarouselID === carouselID;
      });

      const carouselUrls = carouselImages
        .map((media) => (typeof media === "string" ? media : media.url))
        .filter(Boolean);

      // Verificar si todas las imágenes del carousel están seleccionadas
      const allSelected = carouselUrls.every((u) => selectedImages.includes(u));

      setSelectedImages((prev) => {
        if (allSelected) {
          // Deseleccionar todas las imágenes del carousel
          return prev.filter((u) => !carouselUrls.includes(u));
        } else {
          // Seleccionar todas las imágenes del carousel
          const newUrls = carouselUrls.filter((u) => !prev.includes(u));
          return [...prev, ...newUrls];
        }
      });
    } else {
      // Imagen individual
      setSelectedImages((prev) => {
        if (prev.includes(url)) {
          return prev.filter((u) => u !== url);
        } else {
          return [...prev, url];
        }
      });
    }
  };

  const onImportSelectedMediaToGallery = async (selectedMediaUrls) => {
    if (!selectedMediaUrls || selectedMediaUrls.length === 0) {
      return false;
    }

    const selectedUrlSet = new Set(selectedMediaUrls);
    const itemsToUpload = [];
    const processedCarouselIds = new Set();
    const galleryContentType = getGalleryContentTypeFromMediaType(mediaType);
    const individualGalleryType =
      galleryContentType === FILTERS_MEDIA_CONTENT_TYPE.carousel
        ? FILTERS_MEDIA_CONTENT_TYPE.image
        : galleryContentType;
    const galleryIndexFieldsForType = (type) =>
      getGalleryIndexedMediaFields({ type, aspectRatio });

    const getLegacySelectedUrlsInModalOrder = ({ legacyCarouselId }) => {
      const urls = [];
      generatedMedia.forEach((media) => {
        if (
          typeof media === "object" &&
          (media.status || media.resizingFormat)
        ) {
          return;
        }
        const url = typeof media === "string" ? media : media?.url;
        const carouselID =
          typeof media === "object" && media.carouselID
            ? media.carouselID
            : null;
        if (carouselID === legacyCarouselId && url && selectedUrlSet.has(url)) {
          urls.push(url);
        }
      });
      return urls;
    };

    generatedMedia.forEach((media) => {
      if (typeof media === "object" && (media.status || media.resizingFormat)) {
        return;
      }

      if (media.type === "carousel" && Array.isArray(media.urls)) {
        const carouselID = media.carouselID;
        const hasSelectedUrl = media.urls.some((url) =>
          selectedUrlSet.has(url)
        );

        if (hasSelectedUrl && !processedCarouselIds.has(carouselID)) {
          const carouselRatings = media.urls.map(
            (carouselUrl) => ratings?.[carouselUrl] ?? null
          );
          itemsToUpload.push({
            ...galleryIndexFieldsForType(FILTERS_MEDIA_CONTENT_TYPE.carousel),
            urls: media.urls,
            ratings: carouselRatings,
            isAIGenerated: true,
          });
          processedCarouselIds.add(carouselID);
        }
        return;
      }

      const url = typeof media === "string" ? media : media?.url;
      if (!url || !selectedUrlSet.has(url)) return;

      const legacyCarouselId =
        typeof media === "object" && media.carouselID ? media.carouselID : null;

      if (legacyCarouselId) {
        if (processedCarouselIds.has(legacyCarouselId)) return;

        const legacyUrls = getLegacySelectedUrlsInModalOrder({
          legacyCarouselId,
        });
        if (legacyUrls.length === 0) return;

        const carouselRatings = legacyUrls.map(
          (legacyUrl) => ratings?.[legacyUrl] ?? null
        );
        itemsToUpload.push({
          ...galleryIndexFieldsForType(FILTERS_MEDIA_CONTENT_TYPE.carousel),
          urls: legacyUrls,
          ratings: carouselRatings,
          isAIGenerated: true,
        });
        processedCarouselIds.add(legacyCarouselId);
        return;
      }

      itemsToUpload.push({
        ...galleryIndexFieldsForType(individualGalleryType),
        url,
        rating: ratings?.[url] ?? null,
        isAIGenerated: true,
        ...(typeof media === "object" &&
        (media.sourceUrl || media.originalVideoUrl) &&
        (media.sourceUrl || media.originalVideoUrl) !== url
          ? { sourceUrl: media.sourceUrl || media.originalVideoUrl }
          : {}),
        ...(typeof media === "object" && media.subtitles
          ? { subtitles: media.subtitles }
          : {}),
      });
    });

    if (itemsToUpload.length === 0) {
      return false;
    }

    if (onCallbackCampaignMediaImport) {
      const campaignEntries =
        campaignMediaEntriesFromAISaveItems(itemsToUpload);
      if (campaignEntries.length > 0) {
        onCallbackCampaignMediaImport({ entries: campaignEntries });
      }
    }

    const itemsToSave = contentFolderID
      ? itemsToUpload.map((item) => ({
          ...item,
          folderID: contentFolderID,
        }))
      : itemsToUpload;
    const saveResult = await dispatch(
      startAddContentsToList(itemsToSave, { preserveOrder: true })
    );

    if (!saveResult) {
      return false;
    }

    SimpleAlert({
      title: t(MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY),
      icon: "success",
    });

    onCloseModal();

    if (onCallbackAfterSaveToGallery && !onCallbackCampaignMediaImport) {
      onCallbackAfterSaveToGallery();
    }

    return true;
  };

  const onCallbackEditItem = async (
    savePayload,
    itemEditingIndex,
    editData = {}
  ) => {
    detachFromIncomingInitialValuesRef.current = true;
    const editedUrl =
      typeof savePayload === "string" ? savePayload : savePayload?.url;
    const mode =
      typeof savePayload === "string"
        ? "replace"
        : savePayload?.mode || "replace";
    const editedFields =
      typeof savePayload === "object" && savePayload
        ? {
            ...(savePayload.sourceUrl
              ? { sourceUrl: savePayload.sourceUrl }
              : {}),
            ...(savePayload.subtitles
              ? { subtitles: savePayload.subtitles }
              : {}),
            ...(savePayload.duration != null
              ? { duration: savePayload.duration }
              : {}),
          }
        : {};

    if (!editedUrl) return;

    if (mode === "new") {
      const nextItem =
        Object.keys(editedFields).length > 0
          ? { url: editedUrl, ...editedFields }
          : editedUrl;
      updateGeneratedMedia((prev) => [nextItem, ...prev]);
      notifyGeneratedGallerySaved([editedUrl]);
      return;
    }

    const sourceUrl = typeof editData?.url === "string" ? editData.url : "";
    const isCarouselEdit =
      editData?.isCarousel && editData?.carouselIndex !== undefined;
    const carouselIndex = editData?.carouselIndex;

    updateGeneratedMedia((prev) => {
      const matchIndex = findGeneratedMediaIndex({
        items: prev,
        editData,
        fallbackIndex: itemEditingIndex,
      });
      if (matchIndex < 0) return prev;

      const currentItem = prev[matchIndex];
      if (
        isCarouselEdit &&
        currentItem?.type === "carousel" &&
        Array.isArray(currentItem.urls)
      ) {
        const updatedUrls = [...currentItem.urls];
        if (updatedUrls[carouselIndex] !== undefined) {
          updatedUrls[carouselIndex] = editedUrl;
        }
        const carouselItem = { ...currentItem, urls: updatedUrls };
        const rest = prev.filter((_, index) => index !== matchIndex);
        return [carouselItem, ...rest];
      }

      const updatedItem =
        typeof currentItem === "string"
          ? Object.keys(editedFields).length > 0
            ? { url: editedUrl, ...editedFields }
            : editedUrl
          : { ...currentItem, url: editedUrl, ...editedFields };
      const rest = prev.filter((_, index) => index !== matchIndex);
      return [updatedItem, ...rest];
    });

    if (sourceUrl && selectedImages.includes(sourceUrl)) {
      setSelectedImages((prev) =>
        prev.map((url) => (url === sourceUrl ? editedUrl : url))
      );
    }

    notifyGeneratedGallerySaved([
      isCarouselEdit && typeof editData?.itemId === "string" && editData.itemId
        ? editData.itemId
        : editedUrl,
    ]);
  };

  const onSelectAllMedia = () => {
    const allMediaUrls = [];

    generatedMedia.forEach((media) => {
      if (media.type === "carousel" && Array.isArray(media.urls)) {
        allMediaUrls.push(...media.urls);
      } else if (typeof media === "string") {
        allMediaUrls.push(media);
      } else if (
        typeof media === "object" &&
        media.url &&
        !media.status &&
        !media.resizingFormat
      ) {
        allMediaUrls.push(media.url);
      }
    });

    const allSelected =
      allMediaUrls.length > 0 &&
      allMediaUrls.every((url) => selectedImages.includes(url));

    if (allSelected) {
      setSelectedImages([]);
    } else {
      setSelectedImages(allMediaUrls);
    }
  };

  const onTrialExpandContent = (item) => {
    setTrialCTACreatives([item]);
    setTrialCTAPopupOpen(true);
  };

  const onTrialDownloadContent = (urls = []) => {
    setTrialCTACreatives(urls);
    setTrialCTAPopupOpen(true);
  };

  const onTrialCreatePost = (item) => {
    setTrialCTAPopupOpen(false);
    if (item) {
      const shareId = openShare({
        item,
        onCloseModal: () => {
          nestedModalIdsRef.current = nestedModalIdsRef.current.filter(
            (id) => id !== shareId
          );
        },
      });
      nestedModalIdsRef.current.push(shareId);
    }
  };

  const onClearTrialContentCTAPopup = () => {
    setTrialCTAPopupOpen(false);
  };

  const pendingGenerationIDs = useMemo(
    () => getPendingGenerationIDs(generatedMedia),
    [generatedMedia]
  );
  const pendingGenerationsCount = pendingGenerationIDs.size;
  const hasPendingGenerations = pendingGenerationsCount > 0;

  const onDirectClose = async () => {
    if (!isMountedRef.current) {
      return;
    }

    if (generatedMedia.length > 0) {
      const response = await ConfirmAlert({
        title: t(MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TITLE),
        text: hasPendingGenerations
          ? `${t(MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TEXT)} ${t(
              MODAL_CREATE_WITH_IA_YOU_CAN_LEAVE_THE_PAGE
            )}`
          : t(MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TEXT),
        showCancelButton: true,
        confirmButtonText: t(BUTTON_YES_I_SURE),
        cancelButtonText: t(BUTTON_GO_BACK),
      });
      if (response.isConfirmed) {
        onClearTrialContentCTAPopup();
        onCloseModal();
      }
    } else {
      onClearTrialContentCTAPopup();
      onCloseModal();
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    getBrandInformation();
    getSubtitlesConfiguration();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onDirectClose}
        fullWidth={true}
        fullScreen
        scroll="paper"
        aria-label={t(MODAL_CREATE_WITH_IA_ARIA_LABEL)}
        hasTitle={false}
        sxContent={{
          py: 0,
          px: 0,
          display: "flex",
          flexDirection: "row",
          height: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <ResponsiveConfigContainer
          isSmallScreen={isSmallScreen}
          open={isConfigOpen}
          onClose={toggleConfig}
        >
          {isConfigOpen && (
            <ConfigurationPanel
              control={control}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              clearErrors={clearErrors}
              watch={watch}
              isPreparingSubmit={isPreparingSubmit}
              pendingGenerationsCount={pendingGenerationsCount}
              onSubmit={handleSubmit(onSubmit)}
              hasReferenceImage={hasReferenceImage}
              onOpenGallery={onOpenGallery}
              onOpenGalleryForReferenceAd={onOpenGalleryForReferenceAd}
              isGalleryOpen={isGalleryOpen}
              onToggleConfig={toggleConfig}
              businessOffering={brandInformation?.businessOffering}
              mediaType={mediaType}
              logoUrl={brandInformation?.logoUrl}
              onChangeBrandInformation={setBrandInformation}
              userID={userID}
              onSaveLogo={onSaveLogo}
              defaultAdvancedAccordionExpanded={
                Boolean(initialValues.expandAdvancedConfigurationAccordion) ||
                shouldExpandAdvancedAccordion
              }
              additionalAssets={additionalAssets}
              additionalAssetsEnabledMap={additionalAssetsEnabledMap}
              onToggleAdditionalAsset={onToggleAdditionalAsset}
              brandInformation={brandInformation}
              preserveHasLogo={typeof initialValues.hasLogo === "boolean"}
            />
          )}
        </ResponsiveConfigContainer>

        <Box
          ref={galleryPanelRef}
          sx={{
            flex: 1,
            p: { xs: 2, sm: 4 },
            pt: { xs: 6, sm: 6 },
            overflowY: "auto",
            position: "relative",
            height: "100vh",
            maxHeight: "100vh",
          }}
        >
          {!isConfigOpen && (
            <IconButton
              onClick={toggleConfig}
              sx={{
                position: "absolute",
                top: 15,
                left: 10,
                zIndex: 10,
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <IconButton
            onClick={onDirectClose}
            sx={{
              position: "absolute",
              top: 15,
              right: 6,
            }}
          >
            <CloseIcon />
          </IconButton>

          {generatedMedia.length > 0 ? (
            <GeneratedImagesGallery
              pendingGenerationsCount={pendingGenerationsCount}
              ratings={ratings}
              generatedMedia={generatedMedia}
              highlightedIds={highlightedGalleryKeys}
              onChangeGeneratedMedia={updateGeneratedMedia}
              onGenerateMore={handleSubmit(onSubmit)}
              onRetryGeneration={onRetryGeneration}
              onDismissGeneration={onDismissGeneration}
              getPendingSpendForAction={getPendingSpendForAction}
              onReserveActionSpend={onReserveActionSpend}
              onReleaseActionSpend={onReleaseActionSpend}
              onLocalMediaChanged={onDetachIncomingInitialValues}
              onSelectMedia={onSelectMediaToggle}
              onImportToGallery={onImportSelectedMediaToGallery}
              onSelectAllMedia={onSelectAllMedia}
              selectedImages={selectedImages}
              multiple={true}
              onCallbackEditItem={onCallbackEditItem}
              mediaType={mediaType}
              aspectRatio={aspectRatio}
              numberOfImages={numberOfImages}
              isTrial={isTrial}
              onTrialExpandContent={onTrialExpandContent}
              onTrialDownloadContent={onTrialDownloadContent}
              onClearTrialContentCTAPopup={onClearTrialContentCTAPopup}
            />
          ) : (
            <EmptyState />
          )}
        </Box>
        {isTrial && trialCTAPopupOpen && (
          <TrialContentCTAPopup
            modalOpen={trialCTAPopupOpen}
            onCloseModal={onClearTrialContentCTAPopup}
            creatives={trialCTACreatives}
            onCreatePost={onTrialCreatePost}
          />
        )}
      </ModalBasicLayout>
    </>
  );
};

export default ModalCreateWithIA;

// Internal container for responsive config panel rendering
const ResponsiveConfigContainer = ({
  isSmallScreen,
  open,
  onClose,
  children,
}) => {
  if (isSmallScreen) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        variant="temporary"
        anchor="left"
        ModalProps={{
          keepMounted: true,
          disablePortal: true,
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxWidth: "90vw",
            height: "100%",
            zIndex: APP_DROPDOWN_Z_INDEX,
          },
        }}
      >
        {children}
      </Drawer>
    );
  }

  return children;
};
